import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconArrowLeftWhite from 'components/icons/Slider/iconArrowLeftWhite';
import IconArrowRightWhite from 'components/icons/Slider/iconArrowRightWhite';
import VisuallyHidden from 'components/consumer/VisuallyHidden';
import useIsHoverDevice from 'hooks/useIsHoverDevice';
import useTemporaryReveal from 'hooks/useTemporaryReveal';
const EMPTY_ARRAY = [];

// Falls back to the unversioned Cylindo URL when the featured one 404s
const DimensionShotImage = t0 => {
  const $ = _c(15);
  let imgProps;
  let shot;
  if ($[0] !== t0) {
    ({
      shot,
      ...imgProps
    } = t0);
    $[0] = t0;
    $[1] = imgProps;
    $[2] = shot;
  } else {
    imgProps = $[1];
    shot = $[2];
  }
  const [imageSrc, setImageSrc] = useState(shot?.src);
  const [hasErrored, setHasErrored] = useState(false);
  let t1;
  if ($[3] !== shot.src) {
    t1 = () => {
      setImageSrc(shot?.src);
      setHasErrored(false);
    };
    $[3] = shot.src;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = shot?.src;
  let t3;
  if ($[5] !== t2) {
    t3 = [t2];
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useEffect(t1, t3);
  let t4;
  if ($[7] !== hasErrored || $[8] !== shot.baseUrl) {
    t4 = () => {
      if (!hasErrored && shot?.baseUrl) {
        setImageSrc(shot.baseUrl);
        setHasErrored(true);
      }
    };
    $[7] = hasErrored;
    $[8] = shot.baseUrl;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleImageError = t4;
  const t5 = `Product dimensions - ${shot.dimensionCode}`;
  let t6;
  if ($[10] !== handleImageError || $[11] !== imageSrc || $[12] !== imgProps || $[13] !== t5) {
    t6 = <img {...imgProps} src={imageSrc} alt={t5} className="h-full w-full object-contain object-center select-none" onError={handleImageError} />;
    $[10] = handleImageError;
    $[11] = imageSrc;
    $[12] = imgProps;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
DimensionShotImage.propTypes = {
  shot: PropTypes.shape({
    src: PropTypes.string.isRequired,
    baseUrl: PropTypes.string,
    dimensionCode: PropTypes.string.isRequired
  })
};

// Scroll-snap track, the same paging the expanded lifestyle gallery uses, so a
// swipe feels identical in both. Scroll position is the source of truth and the
// selected index follows it. A snap scroller cannot wrap, so the arrows stop at
// each end rather than cycling.
const PDPDimensionShotViewer = ({
  dimensionShots = EMPTY_ARRAY,
  selectedDimensionShotIndex = 0,
  onIndexChange,
  className,
  previousArrowClass = '-left-3 md:left-4',
  nextArrowClass = '-right-3 md:right-4',
  arrowButtonClass = 'w-10 h-10 bg-white border border-gray-light6 hover:border-brand [&_svg_path]:fill-gray group-hover/SliderButton:[&_svg_path]:fill-[#2f2f2e]',
  ...imageProps
}) => {
  const scrollerRef = useRef(null);
  // Last index the scroller settled on, so onIndexChange fires once per shot
  const settledIndexRef = useRef(selectedDimensionShotIndex);
  const hasMultipleShots = dimensionShots.length > 1;

  // A pointer device keeps the arrows up, since there is no tap to re-show them
  const isHoverDevice = useIsHoverDevice();
  const {
    isRevealed,
    reveal
  } = useTemporaryReveal({});
  const areArrowsVisible = isHoverDevice || isRevealed;
  const shotCount = dimensionShots.length;
  const scrollToShot = useCallback((index, smooth = true) => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    const clamped = Math.max(0, Math.min(index, shotCount - 1));
    scroller.scrollTo({
      left: clamped * scroller.clientWidth,
      behavior: smooth ? 'smooth' : 'auto'
    });
  }, [shotCount]);

  // The index outlives the viewer, so jump back to it before the first paint
  useLayoutEffect(() => {
    if (settledIndexRef.current > 0) {
      scrollToShot(settledIndexRef.current, false);
    }
    // Only on mount: afterwards the scroller owns its own position
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return undefined;
    }
    let frame = null;
    const handleScroll = () => {
      if (frame) {
        return;
      }

      // Coalesced to one read per frame, since scroll fires far more often
      frame = requestAnimationFrame(() => {
        frame = null;
        const {
          clientWidth
        } = scroller;
        if (!clientWidth) {
          return;
        }
        const index = Math.round(scroller.scrollLeft / clientWidth);
        if (index !== settledIndexRef.current) {
          settledIndexRef.current = index;
          onIndexChange?.(index);
          reveal();
        }
      });
    };
    scroller.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      scroller.removeEventListener('scroll', handleScroll);
    };
  }, [onIndexChange, reveal]);
  useEffect(() => {
    if (!hasMultipleShots) return undefined;
    const handleKeyDown = event => {
      if (event.key === 'ArrowLeft') {
        scrollToShot(settledIndexRef.current - 1);
      } else if (event.key === 'ArrowRight') {
        scrollToShot(settledIndexRef.current + 1);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasMultipleShots, scrollToShot]);

  // Extract and filter out invalid DOM props
  const {
    enableCrops,
    crops,
    type,
    lazy,
    ...validImgProps
  } = imageProps || {};
  const loading = typeof lazy === 'boolean' ? lazy ? 'lazy' : 'eager' : lazy;
  if (!dimensionShots.length) {
    return null;
  }
  return <div className={classNames('relative w-full h-full', className)}>
      <div ref={scrollerRef} onTouchStart={reveal} className="h-full flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {dimensionShots.map(shot => <div key={shot.id}
      // snap-always caps a hard flick at one shot
      className="h-full w-full shrink-0 snap-center snap-always">
            <DimensionShotImage {...validImgProps} loading={loading} shot={shot} />
          </div>)}
      </div>

      {hasMultipleShots &&
    // Faded rather than unmounted, so they can come back without popping
    <div className={classNames('transition-opacity duration-[400ms] ease-out', areArrowsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none')} aria-hidden={!areArrowsVisible}>
          {selectedDimensionShotIndex > 0 && <button type="button" onClick={() => scrollToShot(settledIndexRef.current - 1)}
      // Faded arrows stay in the DOM, so drop them out of the tab order too
      tabIndex={areArrowsVisible ? undefined : -1} className={classNames('group/SliderButton absolute top-1/2 -translate-y-1/2 rounded-full flex justify-center items-center transition-all duration-200 [&_svg_path]:transition-all [&_svg_path]:duration-200', arrowButtonClass, previousArrowClass)} aria-label="Previous image">
              <IconArrowLeftWhite width={20} height={20} />
              <VisuallyHidden>Previous image</VisuallyHidden>
            </button>}

          {selectedDimensionShotIndex < shotCount - 1 && <button type="button" onClick={() => scrollToShot(settledIndexRef.current + 1)} tabIndex={areArrowsVisible ? undefined : -1} className={classNames('group/SliderButton absolute top-1/2 -translate-y-1/2 rounded-full flex justify-center items-center transition-all duration-200 [&_svg_path]:transition-all [&_svg_path]:duration-200', arrowButtonClass, nextArrowClass)} aria-label="Next image">
              <IconArrowRightWhite width={20} height={20} />
              <VisuallyHidden>Next image</VisuallyHidden>
            </button>}
        </div>}
    </div>;
};
PDPDimensionShotViewer.propTypes = {
  dimensionShots: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    src: PropTypes.string.isRequired,
    baseUrl: PropTypes.string,
    dimensionCode: PropTypes.string.isRequired
  })),
  selectedDimensionShotIndex: PropTypes.number,
  onIndexChange: PropTypes.func,
  className: PropTypes.string,
  previousArrowClass: PropTypes.string,
  nextArrowClass: PropTypes.string,
  arrowButtonClass: PropTypes.string
};
export default PDPDimensionShotViewer;